import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Paper,
  List,
  ListItem,
  ListItemText,
  Chip,
  Alert,
  CircularProgress,
  TextField,

  LinearProgress,
} from '@mui/material';
import { useCreateLocationMutation, useUpdateLocationMutation, useGetAllLocationsQuery } from '../redux/adminApiSlice';

// Extracted neighborhood names from the Manhattan map
const MANHATTAN_NEIGHBORHOODS = [
  'Inwood',
  'Fort George',
  'Hudson Heights',
  'Washington Heights',
  'Sugar Hill',
  'Hamilton Heights',
  'Manhattanville',
  'Morningside Heights',
  'Harlem',
  'East Harlem',
  'Spanish Harlem (El Barrio)',
  'Randalls Island',
  'Roosevelt Island',
  'Upper East Side',
  'Yorkville',
  'Carnegie Hill',
  'Upper West Side',
  'Lincoln Square',
  'Hell\'s Kitchen',
  'Midtown West',
  'Midtown',
  'Midtown East',
  'Murray Hill',
  'Kips Bay',
  'Gramercy Park',
  'Flatiron District',
  'Chelsea',
  'Meatpacking District',
  'West Village',
  'Greenwich Village',
  'NoHo',
  'SoHo',
  'Nolita',
  'Little Italy',
  'Chinatown',
  'Tribeca',
  'Battery Park City',
  'Financial District',
  'South Street Seaport',
  'Lower East Side',
  'East Village',
  'Alphabet City',
  'Stuyvesant Town',
  'Peter Cooper Village',
  'Turtle Bay',
  'Sutton Place',
  'Beekman Place',
  'Tudor City',
];

interface NeighborhoodImporterProps {}

const NeighborhoodImporter: React.FC<NeighborhoodImporterProps> = () => {
  const [selectedNeighborhoods, setSelectedNeighborhoods] = useState<string[]>([]);
  const [parentLocationId, setParentLocationId] = useState<string>('');
  const [isImporting, setIsImporting] = useState(false);
  const [currentProgress, setCurrentProgress] = useState({ current: 0, total: 0, name: '' });
  const [importResults, setImportResults] = useState<{
    success: string[];
    failed: { name: string; error: string }[];
  }>({ success: [], failed: [] });
  const [selectedExistingNeighborhoods, setSelectedExistingNeighborhoods] = useState<string[]>([]);
  
  const [createLocation] = useCreateLocationMutation();
  const [updateLocation] = useUpdateLocationMutation();
  const { data: allLocations, isLoading: locationsLoading } = useGetAllLocationsQuery();

  // Find New York City and existing neighborhoods
  const nycLocation = allLocations?.find(loc => 
    (loc.name.toLowerCase().includes('new york city') || loc.name.toLowerCase() === 'new york city') && 
    (loc.level === 'CITY' || loc.level === 'City')
  );

  const existingManhattanNeighborhoods = allLocations?.filter(loc => 
    (loc.level === 'NEIGHBORHOOD' || loc.level === 'Neighborhood') &&
    loc.parent === nycLocation?._id
  ) || [];

  const existingNeighborhoodNames = existingManhattanNeighborhoods.map(n => n.name);
  const missingNeighborhoods = MANHATTAN_NEIGHBORHOODS.filter(name => 
    !existingNeighborhoodNames.some(existing => 
      existing.toLowerCase() === name.toLowerCase()
    )
  );

  // Set New York City as default parent if found
  React.useEffect(() => {
    if (nycLocation && !parentLocationId) {
      setParentLocationId(nycLocation._id);
    }
  }, [nycLocation, parentLocationId]);

  // Function to validate bounds against existing neighborhoods
  const validateBoundsAgainstExisting = (newBounds: any, neighborhoodName: string) => {
    const neighborhoodsWithBounds = existingManhattanNeighborhoods.filter(n => n.boundingBox);
    
    if (neighborhoodsWithBounds.length === 0) {
      return { isValid: true, message: 'No reference bounds available' };
    }

    // Check if the new bounds are reasonable compared to existing ones
    const newArea = Math.abs(
      (newBounds.northEast.lat - newBounds.southWest.lat) * 
      (newBounds.northEast.lng - newBounds.southWest.lng)
    );

    const existingAreas = neighborhoodsWithBounds.map(n => {
      if (!n.boundingBox?.coordinates?.[0]) return 0;
      const coords = n.boundingBox.coordinates[0];
      const lats = coords.map(c => c[1]);
      const lngs = coords.map(c => c[0]);
      return Math.abs((Math.max(...lats) - Math.min(...lats)) * (Math.max(...lngs) - Math.min(...lngs)));
    }).filter(area => area > 0);

    if (existingAreas.length > 0) {
      const avgArea = existingAreas.reduce((a, b) => a + b, 0) / existingAreas.length;
      const ratio = newArea / avgArea;
      
      if (ratio > 5) {
        return { 
          isValid: false, 
          message: `Bounds seem too large (${ratio.toFixed(1)}x average)` 
        };
      } else if (ratio < 0.1) {
        return { 
          isValid: false, 
          message: `Bounds seem too small (${ratio.toFixed(2)}x average)` 
        };
      }
    }

    return { isValid: true, message: 'Bounds look reasonable' };
  };

  // Geocoding function similar to the one in LocationEditorTab
  const fetchNeighborhoodBounds = async (neighborhoodName: string): Promise<any> => {
    return new Promise((resolve, reject) => {
      if (!window.google || !window.google.maps || !window.google.maps.Geocoder) {
        reject(new Error('Google Maps Geocoder not loaded'));
        return;
      }
      
      const geocoder = new window.google.maps.Geocoder();
      const searchQuery = `${neighborhoodName} neighborhood, Manhattan, New York City, NY`;

      const request: google.maps.GeocoderRequest = {
        address: searchQuery,
        componentRestrictions: { country: 'US' },
        bounds: new window.google.maps.LatLngBounds(
          { lat: 40.699392, lng: -74.020208 }, // Manhattan bounds
          { lat: 40.878195, lng: -73.907005 }
        )
      };

      geocoder.geocode(request, (results, status) => {
        if (status === 'OK' && results && results.length > 0) {
          const result = results[0];
          if (result.geometry) {
            const bounds = result.geometry.bounds || result.geometry.viewport;
            
            if (bounds) {
              const ne = bounds.getNorthEast();
              const sw = bounds.getSouthWest();
              
              const boundingBox = {
                northEast: { lat: ne.lat(), lng: ne.lng() },
                southWest: { lat: sw.lat(), lng: sw.lng() }
              };
              
              const center = {
                lat: (ne.lat() + sw.lat()) / 2,
                lng: (ne.lng() + sw.lng()) / 2
              };
              
              resolve({
                boundingBox,
                center,
                formattedAddress: result.formatted_address
              });
            } else {
              reject(new Error('No bounds found in geocoding result'));
            }
          } else {
            reject(new Error('No geometry found in geocoding result'));
          }
        } else {
          reject(new Error(`Geocoding failed: ${status}`));
        }
      });
    });
  };

  const handleSelectAllMissing = () => {
    setSelectedNeighborhoods(missingNeighborhoods);
  };

  const handleDeselectAll = () => {
    setSelectedNeighborhoods([]);
  };

  const handleToggleExistingNeighborhood = (neighborhoodId: string) => {
    setSelectedExistingNeighborhoods(prev => 
      prev.includes(neighborhoodId)
        ? prev.filter(id => id !== neighborhoodId)
        : [...prev, neighborhoodId]
    );
  };

  const handleSelectAllExisting = () => {
    setSelectedExistingNeighborhoods(existingManhattanNeighborhoods.map(n => n._id));
  };

  const handleDeselectAllExisting = () => {
    setSelectedExistingNeighborhoods([]);
  };

  const handleToggleNeighborhood = (neighborhood: string) => {
    setSelectedNeighborhoods(prev => 
      prev.includes(neighborhood)
        ? prev.filter(n => n !== neighborhood)
        : [...prev, neighborhood]
    );
  };

  const handleImport = async () => {
    if (!parentLocationId) {
      alert('Please select a parent location (New York City)');
      return;
    }

    const totalOperations = selectedNeighborhoods.length + selectedExistingNeighborhoods.length;
    if (totalOperations === 0) {
      alert('Please select at least one neighborhood to process');
      return;
    }

    setIsImporting(true);
    setCurrentProgress({ current: 0, total: totalOperations, name: '' });
    const results = { success: [], failed: [] };

    let currentIndex = 0;

    // First, create new neighborhoods
    for (let i = 0; i < selectedNeighborhoods.length; i++) {
      const neighborhood = selectedNeighborhoods[i];
      currentIndex++;
      setCurrentProgress({ current: currentIndex, total: totalOperations, name: `Creating ${neighborhood}` });
      
      try {
        console.log(`[NeighborhoodImporter] Processing neighborhood ${i + 1}/${selectedNeighborhoods.length}: ${neighborhood}`);
        
        // First, geocode the neighborhood to get bounds
        let geocodeData;
        try {
          geocodeData = await fetchNeighborhoodBounds(neighborhood);
          console.log(`[NeighborhoodImporter] Geocoded ${neighborhood}:`, geocodeData);
          
          // Validate bounds against existing neighborhoods
          if (geocodeData?.boundingBox) {
            const validation = validateBoundsAgainstExisting(geocodeData.boundingBox, neighborhood);
            console.log(`[NeighborhoodImporter] Bounds validation for ${neighborhood}:`, validation);
            
            if (!validation.isValid) {
              console.warn(`[NeighborhoodImporter] Bounds validation warning for ${neighborhood}: ${validation.message}`);
              // Continue anyway but log the warning
            }
          }
        } catch (geocodeError: any) {
          console.warn(`[NeighborhoodImporter] Geocoding failed for ${neighborhood}:`, geocodeError);
          // Continue without bounds if geocoding fails
          geocodeData = null;
        }

        // Create the location with bounds if available
        const locationData: any = {
          name: neighborhood,
          level: 'NEIGHBORHOOD',
          parent: parentLocationId,
        };

        // Add geocoded data if available
        if (geocodeData) {
          locationData.boundingBox = geocodeData.boundingBox;
          locationData.center = geocodeData.center;
          locationData.formattedAddress = geocodeData.formattedAddress;
        }

        await createLocation(locationData).unwrap();
        (results.success as string[]).push(neighborhood);
        console.log(`[NeighborhoodImporter] Successfully created: ${neighborhood}`);
        
        // Small delay to avoid hitting API limits
        await new Promise(resolve => setTimeout(resolve, 200));
        
      } catch (error: any) {
        console.error(`[NeighborhoodImporter] Failed to create ${neighborhood}:`, error);
        (results.failed as { name: string; error: string }[]).push({
          name: neighborhood,
          error: error?.data?.message || error?.message || 'Unknown error'
        });
      }
    }

    // Then, update existing neighborhoods
    for (let i = 0; i < selectedExistingNeighborhoods.length; i++) {
      const neighborhoodId = selectedExistingNeighborhoods[i];
      const neighborhood = existingManhattanNeighborhoods.find(n => n._id === neighborhoodId);
      
      if (!neighborhood) continue;
      
      currentIndex++;
      setCurrentProgress({ current: currentIndex, total: totalOperations, name: `Updating ${neighborhood.name}` });
      
      try {
        console.log(`[NeighborhoodImporter] Updating existing neighborhood ${i + 1}/${selectedExistingNeighborhoods.length}: ${neighborhood.name}`);
        
        // Geocode the neighborhood to get new bounds
        let geocodeData;
        try {
          geocodeData = await fetchNeighborhoodBounds(neighborhood.name);
          console.log(`[NeighborhoodImporter] Geocoded ${neighborhood.name}:`, geocodeData);
          
          // Validate bounds against existing neighborhoods
          if (geocodeData?.boundingBox) {
            const validation = validateBoundsAgainstExisting(geocodeData.boundingBox, neighborhood.name);
            console.log(`[NeighborhoodImporter] Bounds validation for ${neighborhood.name}:`, validation);
            
            if (!validation.isValid) {
              console.warn(`[NeighborhoodImporter] Bounds validation warning for ${neighborhood.name}: ${validation.message}`);
              // Continue anyway but log the warning
            }
          }
        } catch (geocodeError: any) {
          console.warn(`[NeighborhoodImporter] Geocoding failed for ${neighborhood.name}:`, geocodeError);
          // Continue without bounds if geocoding fails
          geocodeData = null;
        }

        // Update the location with new bounds if available
        const updateData: any = {};

        // Add geocoded data if available
        if (geocodeData) {
          if (geocodeData.boundingBox) {
            updateData.boundingBox = {
              type: 'Polygon',
              coordinates: [[
                [geocodeData.boundingBox.southWest.lng, geocodeData.boundingBox.southWest.lat],
                [geocodeData.boundingBox.northEast.lng, geocodeData.boundingBox.southWest.lat],
                [geocodeData.boundingBox.northEast.lng, geocodeData.boundingBox.northEast.lat],
                [geocodeData.boundingBox.southWest.lng, geocodeData.boundingBox.northEast.lat],
                [geocodeData.boundingBox.southWest.lng, geocodeData.boundingBox.southWest.lat]
              ]]
            };
          }
          if (geocodeData.center) {
            updateData.coordinates = {
              type: 'Point',
              coordinates: [geocodeData.center.lng, geocodeData.center.lat]
            };
          }
        }

        if (Object.keys(updateData).length > 0) {
          await updateLocation({ id: neighborhoodId, updates: updateData }).unwrap();
          (results.success as string[]).push(`Updated ${neighborhood.name}`);
          console.log(`[NeighborhoodImporter] Successfully updated: ${neighborhood.name}`);
        } else {
          (results.failed as { name: string; error: string }[]).push({
            name: neighborhood.name,
            error: 'No geocoding data available'
          });
        }
        
        // Small delay to avoid hitting API limits
        await new Promise(resolve => setTimeout(resolve, 200));
        
      } catch (error: any) {
        console.error(`[NeighborhoodImporter] Failed to update ${neighborhood.name}:`, error);
        (results.failed as { name: string; error: string }[]).push({
          name: neighborhood.name,
          error: error?.data?.message || error?.message || 'Unknown error'
        });
      }
    }

    setImportResults(results);
    setIsImporting(false);
    setCurrentProgress({ current: 0, total: 0, name: '' });
  };

  if (locationsLoading) {
    return (
      <Box sx={{ p: 3, display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
        <Typography sx={{ ml: 2 }}>Loading existing locations...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Manhattan Neighborhood Importer
      </Typography>
      
      <Typography variant="body1" sx={{ mb: 3 }}>
        Import Manhattan neighborhoods from the neighborhood map. This tool compares the map with your existing database.
      </Typography>

      {/* Status Overview */}
      <Paper sx={{ p: 2, mb: 3, bgcolor: '#f5f5f5' }}>
        <Typography variant="h6" gutterBottom>
          Database Status
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Chip 
            label={`${existingManhattanNeighborhoods.length} Existing Neighborhoods`} 
            color="success" 
            variant="filled"
          />
          <Chip 
            label={`${missingNeighborhoods.length} Missing from Database`} 
            color="warning" 
            variant="filled"
          />
          <Chip 
            label={`${MANHATTAN_NEIGHBORHOODS.length} Total on Map`} 
            color="info" 
            variant="outlined"
          />
        </Box>
        {nycLocation && (
          <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
            Parent: {nycLocation.name} (ID: {nycLocation._id})
          </Typography>
        )}
      </Paper>

      {/* Parent Location Selection */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          1. Select Parent Location (New York City)
        </Typography>
        <TextField
          fullWidth
          label="Parent Location ID (New York City)"
          value={parentLocationId}
          onChange={(e) => setParentLocationId(e.target.value)}
          placeholder="Enter New York City's location ID"
          helperText="Find New York City's ID in the Locations page"
        />
      </Paper>

      {/* Neighborhood Selection */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">
            2. Select Missing Neighborhoods ({selectedNeighborhoods.length}/{missingNeighborhoods.length})
          </Typography>
          <Box>
            <Button onClick={handleSelectAllMissing} sx={{ mr: 1 }}>
              Select All Missing
            </Button>
            <Button onClick={handleDeselectAll}>
              Deselect All
            </Button>
          </Box>
        </Box>

        {missingNeighborhoods.length === 0 ? (
          <Alert severity="success">
            🎉 All neighborhoods from the map are already in your database!
          </Alert>
        ) : (
          <>
            <Typography variant="subtitle2" sx={{ mb: 1, color: 'warning.main' }}>
              Missing Neighborhoods ({missingNeighborhoods.length}):
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
              {missingNeighborhoods.map((neighborhood) => (
                <Chip
                  key={neighborhood}
                  label={neighborhood}
                  onClick={() => handleToggleNeighborhood(neighborhood)}
                  color={selectedNeighborhoods.includes(neighborhood) ? 'primary' : 'warning'}
                  variant={selectedNeighborhoods.includes(neighborhood) ? 'filled' : 'outlined'}
                  sx={{ cursor: 'pointer' }}
                />
              ))}
            </Box>
          </>
        )}

        {existingManhattanNeighborhoods.length > 0 && (
          <>
            <Typography variant="subtitle2" sx={{ mb: 1, color: 'success.main' }}>
              Existing Neighborhoods ({existingManhattanNeighborhoods.length}) - Select to Update Bounds:
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
              <Button size="small" onClick={handleSelectAllExisting}>
                Select All Existing
              </Button>
              <Button size="small" onClick={handleDeselectAllExisting}>
                Deselect All Existing
              </Button>
            </Box>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {existingManhattanNeighborhoods.map((neighborhood) => (
                <Chip
                  key={neighborhood._id}
                  label={`${neighborhood.name}${neighborhood.boundingBox ? ' ✓' : ' ⚠️'}`}
                  onClick={() => handleToggleExistingNeighborhood(neighborhood._id)}
                  color={selectedExistingNeighborhoods.includes(neighborhood._id) ? 'primary' : 'success'}
                  variant={selectedExistingNeighborhoods.includes(neighborhood._id) ? 'filled' : 'outlined'}
                  sx={{ cursor: 'pointer' }}
                />
              ))}
            </Box>
            <Typography variant="caption" sx={{ display: 'block', mt: 1, color: 'text.secondary' }}>
              ✓ = Has bounds, ⚠️ = Missing bounds. Click to select for bounds update.
            </Typography>
          </>
        )}
      </Paper>

      {/* Import Button */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          3. Import and Update Selected Neighborhoods
        </Typography>
        
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" sx={{ mb: 1 }}>
            • {selectedNeighborhoods.length} new neighborhoods to create
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            • {selectedExistingNeighborhoods.length} existing neighborhoods to update bounds
          </Typography>
          <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
            Total: {selectedNeighborhoods.length + selectedExistingNeighborhoods.length} operations
          </Typography>
        </Box>

        <Button
          variant="contained"
          onClick={handleImport}
          disabled={isImporting || (selectedNeighborhoods.length === 0 && selectedExistingNeighborhoods.length === 0) || !parentLocationId}
          startIcon={isImporting ? <CircularProgress size={20} /> : null}
        >
          {isImporting ? 'Processing...' : `Process ${selectedNeighborhoods.length + selectedExistingNeighborhoods.length} Neighborhoods`}
        </Button>
        
        {isImporting && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="body2" gutterBottom>
              Processing: {currentProgress.name} ({currentProgress.current}/{currentProgress.total})
            </Typography>
            <LinearProgress 
              variant="determinate" 
              value={(currentProgress.current / currentProgress.total) * 100} 
            />
          </Box>
        )}
      </Paper>

      {/* Results */}
      {(importResults.success.length > 0 || importResults.failed.length > 0) && (
        <Paper sx={{ p: 2 }}>
          <Typography variant="h6" gutterBottom>
            Import Results
          </Typography>
          
          {importResults.success.length > 0 && (
            <Alert severity="success" sx={{ mb: 2 }}>
              Successfully imported {importResults.success.length} neighborhoods:
              <List dense>
                {importResults.success.map((name) => (
                  <ListItem key={name} sx={{ py: 0 }}>
                    <ListItemText primary={name} />
                  </ListItem>
                ))}
              </List>
            </Alert>
          )}

          {importResults.failed.length > 0 && (
            <Alert severity="error">
              Failed to import {importResults.failed.length} neighborhoods:
              <List dense>
                {importResults.failed.map((item) => (
                  <ListItem key={item.name} sx={{ py: 0 }}>
                    <ListItemText 
                      primary={item.name} 
                      secondary={item.error}
                    />
                  </ListItem>
                ))}
              </List>
            </Alert>
          )}
        </Paper>
      )}

      {/* Image Reference */}
      <Paper sx={{ p: 2, mt: 3 }}>
        <Typography variant="h6" gutterBottom>
          Reference Map
        </Typography>
        <img 
          src="/Manhattan-Neighborhoods.webp" 
          alt="Manhattan Neighborhoods Map"
          style={{ 
            maxWidth: '100%', 
            height: 'auto',
            border: '1px solid #ddd',
            borderRadius: '4px'
          }}
        />
      </Paper>
    </Box>
  );
};

export default NeighborhoodImporter;
